import {
  FormActionType,
  FormButtonType,
  GroupAdmin,
  GroupUser,
  SeatUpdateResult,
  UserOperationType,
  UserType,
} from '@components/pages/Organization/types';
import { ACCESS_ANNUAL, ACCESS_PRO } from '@utils/constants';

export const isGroupUser = (user: GroupAdmin | GroupUser): user is GroupUser => {
  return 'accessLevel' in user;
};

export const isGroupAdmin = (user: GroupAdmin | GroupUser): user is GroupAdmin => {
  return !('accessLevel' in user);
};

export const getRecordId = (user: GroupAdmin | GroupUser): number | undefined => {
  if (isGroupAdmin(user)) {
    return user.groupAdminId;
  }
  if (isGroupUser(user)) {
    return user.corporateSubscriptionId;
  }
  return undefined;
};

export const modalHeadlines = {
  [UserType.Admin]: {
    [UserOperationType.CREATE]: 'Add an admin to manage the group subscription',
    [UserOperationType.UPDATE]: 'Edit admin',
  },
  [UserType.Active]: {
    [UserOperationType.CREATE]: 'Add a user to your group subscription',
    [UserOperationType.UPDATE]: 'Edit user',
  },
  [UserType.Archived]: {
    [UserOperationType.UPDATE]: 'Reactivate user',
  },
};

export const modalButtonTexts = {
  [UserType.Admin]: {
    [UserOperationType.CREATE]: {
      [FormButtonType.PRIMARY]: 'Add admin',
    },
    [UserOperationType.UPDATE]: {
      [FormButtonType.PRIMARY]: 'Update',
      [FormButtonType.SECONDARY]: 'Remove admin',
    },
  },
  [UserType.Active]: {
    [UserOperationType.CREATE]: {
      [FormButtonType.PRIMARY]: 'Add user',
    },
    [UserOperationType.UPDATE]: {
      [FormButtonType.PRIMARY]: 'Update',
      [FormButtonType.SECONDARY]: 'Deactivate user',
    },
  },
  [UserType.Archived]: {
    [UserOperationType.UPDATE]: {
      [FormButtonType.PRIMARY]: 'Activate user',
    },
  },
};

export const modalActions = {
  [UserType.Admin]: {
    [UserOperationType.CREATE]: {
      [FormButtonType.PRIMARY]: FormActionType.CREATE_ADMIN,
    },
    [UserOperationType.UPDATE]: {
      [FormButtonType.PRIMARY]: FormActionType.UPDATE_ADMIN,
      [FormButtonType.SECONDARY]: FormActionType.REMOVE_ADMIN,
    },
  },
  [UserType.Active]: {
    [UserOperationType.CREATE]: {
      [FormButtonType.PRIMARY]: FormActionType.CREATE_USER,
    },
    [UserOperationType.UPDATE]: {
      [FormButtonType.PRIMARY]: FormActionType.UPDATE_USER,
      [FormButtonType.SECONDARY]: FormActionType.DEACTIVATE_USER,
    },
  },
  [UserType.Archived]: {
    [UserOperationType.UPDATE]: {
      [FormButtonType.PRIMARY]: FormActionType.REACTIVATE_USER,
    },
  },
};

const calculateSeatAvailability = (
  accessLevel: number,
  currentProSeats: number,
  currentStandardSeats: number,
  totalProSeats: number,
  totalStandardSeats: number
): SeatUpdateResult => {
  if (![ACCESS_PRO, ACCESS_ANNUAL].includes(accessLevel)) {
    return { availableProSeats: currentProSeats, availableStandardSeats: currentStandardSeats };
  }

  if (accessLevel === ACCESS_PRO) {
    return {
      availableProSeats: Math.max(currentProSeats - 1, 0),
      availableStandardSeats: Math.min(currentStandardSeats + 1, totalStandardSeats),
    };
  }

  return {
    availableProSeats: Math.min(currentProSeats + 1, totalProSeats),
    availableStandardSeats: Math.max(currentStandardSeats - 1, 0),
  };
};

const decrementAvailableSeats = (
  accessLevel: number,
  currentProSeats: number,
  currentStandardSeats: number
): SeatUpdateResult => {
  if (accessLevel === ACCESS_PRO) {
    return {
      availableProSeats: Math.max(currentProSeats - 1, 0),
      availableStandardSeats: currentStandardSeats,
    };
  }

  return {
    availableProSeats: currentProSeats,
    availableStandardSeats: Math.max(currentStandardSeats - 1, 0),
  };
};

const incrementAvailableSeats = (
  accessLevel: number,
  currentProSeats: number,
  currentStandardSeats: number
): SeatUpdateResult => {
  if (accessLevel === ACCESS_PRO) {
    return {
      availableProSeats: currentProSeats + 1,
      availableStandardSeats: currentStandardSeats,
    };
  }

  return {
    availableProSeats: currentProSeats,
    availableStandardSeats: currentStandardSeats + 1,
  };
};

const updateUsersList = (users: GroupUser[], userToUpdate: GroupUser) =>
  users.map((existingUser) => (existingUser.id === userToUpdate.id ? userToUpdate : existingUser));

const updateActiveUser = (activeUsers: GroupUser[], user: GroupUser) => {
  const addIfDoesntExists = (users: GroupUser[], userToAdd: GroupUser) => {
    return users.some((existingUser) => existingUser.id === userToAdd.id)
      ? users
      : [userToAdd, ...users];
  };

  let updatedActiveUsers = activeUsers;
  updatedActiveUsers = updateUsersList(activeUsers, user);
  updatedActiveUsers = addIfDoesntExists(updatedActiveUsers, user);

  return updatedActiveUsers;
};

const updateArchivedUser = (archivedUsers: GroupUser[], user: GroupUser) => {
  const updatedArchivedUsers = updateUsersList(archivedUsers, user);

  return updatedArchivedUsers;
};

const updateUserInfo = (users: GroupUser[], admin: GroupAdmin) =>
  users.map((user) =>
    user.id === admin.id ? { ...user, name: admin.name, email: admin.email } : user
  );

export {
  calculateSeatAvailability,
  decrementAvailableSeats,
  incrementAvailableSeats,
  updateActiveUser,
  updateArchivedUser,
  updateUserInfo,
};
